/**
 * Teams API
 */
import { apiClient } from "./client";
import { useQuery } from "@tanstack/react-query";

/** Aligns with API enum: National=0, Distrikt=1, Krets=2, Avdelning=3 */
export type TeamType = 0 | 1 | 2 | 3;

/**
 * API uses JsonStringEnumConverter, so responses use strings like "National".
 * Numeric values are still accepted if serialization changes.
 */
export type TeamTypeFromApi = TeamType | `${TeamType}` | TeamTypeName;

type TeamTypeName =
    | "National"
    | "Distrikt"
    | "Krets"
    | "Avdelning"
    | "national"
    | "distrikt"
    | "krets"
    | "avdelning";

export interface Team {
    id: string;
    name: string;
    description?: string;
    type?: TeamTypeFromApi;
}

/** Map API team type (number or enum string) to tier 0–3 for grouping. */
export function normalizeTeamTypeTier(type: Team["type"] | undefined): TeamType {
    if (typeof type === "number" && type >= 0 && type <= 3) {
        return type as TeamType;
    }
    if (typeof type === "string") {
        const s = type.trim();
        if (s === "0" || s === "1" || s === "2" || s === "3") {
            return Number(s) as TeamType;
        }
        switch (s) {
            case "National":
            case "national":
                return 0;
            case "Distrikt":
            case "distrikt":
                return 1;
            case "Krets":
            case "krets":
                return 2;
            case "Avdelning":
            case "avdelning":
                return 3;
            default:
                return 2;
        }
    }
    return 2;
}

/**
 * Fetches all teams accessible to the current user
 */
export const fetchTeams = async (): Promise<Team[]> => {
    const response = await apiClient.get<Team[]>("/teams");
    return response.data;
};

/**
 * Fetches all teams for reports (AdminOrAbove) - for comparison across teams and total Sweden
 */
export const fetchAllTeamsForReports = async (): Promise<Team[]> => {
    const response = await apiClient.get<Team[]>("/teams/all-for-reports");
    return response.data;
};

/**
 * Hook for fetching teams
 */
export const useTeams = () => {
    return useQuery({
        queryKey: ["teams"],
        queryFn: fetchTeams,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook for fetching all teams for reports page (comparison across teams)
 */
export const useAllTeamsForReports = () => {
    return useQuery({
        queryKey: ["teams", "all-for-reports"],
        queryFn: fetchAllTeamsForReports,
        staleTime: 5 * 60 * 1000,
    });
};